<script setup lang="ts">
import DashboardBlock from '@/components/Dashboard/Body/DashboardBlock.vue'
import DashboardUnifiedChart from '@/components/Dashboard/Body/DashboardUnifiedChart.vue'
import { useDashboardStore } from '@/store/dashboard'
import {
  getDashboardPeriodKey,
  getDashboardPeriodGroupsColumnWeights,
  getDashboardSideBySideGridClass,
  groupDashboardVisibleSearchesByPeriod,
} from '@/utils/dashboard-workspace'
import { ChartBarIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const dashboardStore = useDashboardStore()
const { searches, viewMode, unifiedChartPeriodKeys, scrollToSearchId } = storeToRefs(dashboardStore)

const orderedVisibleSearches = computed(() => dashboardStore.searches.filter((s) => s.visible))

const unifiedKeySet = computed(() => new Set(unifiedChartPeriodKeys.value))

function showInlineChartFor(search: (typeof searches.value)[0]) {
  if (search.type === 'group') return true
  return !unifiedKeySet.value.has(getDashboardPeriodKey(search))
}

function getSensorSearches(searchList: (typeof searches.value)[0][]) {
  return searchList.filter((s) => s.type === 'sensor')
}

function getGroupSearches(searchList: (typeof searches.value)[0][]) {
  return searchList.filter((s) => s.type === 'group')
}

/** Como dispor sensor(es), unificado e grupo(s) no mesmo período (lado a lado). */
type PeriodSensorGroupLayout = 'stack' | 'split-xl-unified-with-groups' | 'split-lg-sensor-with-groups'

type PeriodGroup = { periodKey: string; searches: (typeof searches.value)[0][] }

function getPeriodSensorGroupLayout(group: PeriodGroup): PeriodSensorGroupLayout {
  if (viewMode.value !== 'side-by-side') return 'stack'
  const sensorCount = getSensorSearches(group.searches).length
  const groupCount = getGroupSearches(group.searches).length
  if (sensorCount < 1 || groupCount < 1) return 'stack'
  const hasUnifiedForPeriod = unifiedKeySet.value.has(group.periodKey)
  if (hasUnifiedForPeriod && sensorCount >= 2) return 'split-xl-unified-with-groups'
  return 'split-lg-sensor-with-groups'
}

function periodMixedLayoutOuterClass(layout: PeriodSensorGroupLayout): string {
  if (layout === 'split-xl-unified-with-groups') {
    return 'grid min-w-0 w-full grid-cols-1 items-start gap-6 xl:[grid-template-columns:repeat(3,minmax(0,1fr))]'
  }
  if (layout === 'split-lg-sensor-with-groups') {
    return 'grid min-w-0 w-full max-w-full grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start'
  }
  return ''
}

function periodMixedLayoutLeftColumnClass(layout: PeriodSensorGroupLayout): string {
  const base = 'flex min-w-0 flex-col gap-6'
  if (layout === 'split-xl-unified-with-groups') return `${base} xl:col-span-2`
  return base
}

const workspacePeriodGroups = computed(() => groupDashboardVisibleSearchesByPeriod(orderedVisibleSearches.value))

function groupBlocksLayoutClass(blockCount: number): string {
  if (viewMode.value !== 'side-by-side') {
    return 'flex min-w-0 flex-col gap-6 [&>*]:min-w-0'
  }
  return getDashboardSideBySideGridClass(blockCount)
}

const periodGroupsOuterClass = computed(() => {
  const n = workspacePeriodGroups.value.length
  if (viewMode.value !== 'side-by-side' || n <= 1) {
    return 'flex min-w-0 flex-col gap-8'
  }
  return 'grid min-w-0 gap-6 items-start [&>*]:min-w-0 dashboard-period-groups-weighted'
})

const periodGroupsWeightedStyle = computed((): Record<string, string> | undefined => {
  if (viewMode.value !== 'side-by-side' || workspacePeriodGroups.value.length <= 1) {
    return undefined
  }
  const counts = workspacePeriodGroups.value.map((g) => g.searches.length)
  const weights = getDashboardPeriodGroupsColumnWeights(counts)
  const tpl = weights.map((w) => `minmax(0,${w}fr)`).join(' ')
  return { '--dashboard-pg-cols': tpl }
})

const bodyShellClass = 'flex min-w-0 flex-col gap-6 p-6 lg:px-8'

const compactBlocks = computed(() => viewMode.value === 'side-by-side')

const visibleCount = computed(() => searches.value.filter((s) => s.visible).length)
const hasAnySearch = computed(() => searches.value.length > 0)
const allHidden = computed(() => hasAnySearch.value && visibleCount.value === 0)

watch(scrollToSearchId, (id) => {
  if (!id) return
  nextTick(() => {
    const el = document.getElementById(`dashboard-block-${id}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    dashboardStore.clearScrollToSearchId()
  })
})

onMounted(() => {
  dashboardStore.revivePersistedSearchPeriods()
})
</script>

<template>
  <div :class="bodyShellClass">
    <div
      v-if="!hasAnySearch"
      class="flex min-h-[280px] flex-col items-center justify-center border border-dashed border-stroke bg-gray-2 px-6 py-12 text-center">
      <ChartBarIcon class="mb-4 h-14 w-14 text-gray-300" aria-hidden="true" />
      <p class="font-satoshi text-lg font-bold text-black">{{ t('dashboard.empty.no_selection') }}</p>
      <p class="mt-2 max-w-md text-sm font-medium text-gray-500">{{ t('dashboard.empty.no_selection_hint') }}</p>
    </div>

    <div
      v-else-if="allHidden"
      class="flex min-h-[200px] flex-col items-center justify-center border border-dashed border-stroke bg-gray-2 px-6 py-10 text-center">
      <p class="font-satoshi text-lg font-bold text-black">{{ t('dashboard.empty.all_hidden') }}</p>
      <p class="mt-2 max-w-md text-sm font-medium text-gray-500">{{ t('dashboard.empty.all_hidden_hint') }}</p>
    </div>

    <template v-else>
      <div :class="periodGroupsOuterClass" :style="periodGroupsWeightedStyle">
        <div
          v-for="group in workspacePeriodGroups"
          :key="group.periodKey"
          class="flex w-full min-w-0 max-w-full flex-col gap-6">
          <div
            v-if="getPeriodSensorGroupLayout(group) !== 'stack'"
            :class="periodMixedLayoutOuterClass(getPeriodSensorGroupLayout(group))">
            <div :class="periodMixedLayoutLeftColumnClass(getPeriodSensorGroupLayout(group))">
              <div
                v-if="getSensorSearches(group.searches).length > 0"
                :class="groupBlocksLayoutClass(getSensorSearches(group.searches).length)">
                <DashboardBlock
                  v-for="s in getSensorSearches(group.searches)"
                  :key="s.id"
                  :search="s"
                  :show-inline-chart="showInlineChartFor(s)"
                  :compact-layout="compactBlocks" />
              </div>
              <DashboardUnifiedChart
                v-if="getPeriodSensorGroupLayout(group) === 'split-xl-unified-with-groups' || unifiedKeySet.has(group.periodKey)"
                :period-key="group.periodKey"
                :compact-layout="compactBlocks" />
            </div>
            <div
              v-if="getGroupSearches(group.searches).length > 0"
              class="flex min-w-0 flex-col gap-6">
              <div :class="groupBlocksLayoutClass(getGroupSearches(group.searches).length)">
                <DashboardBlock
                  v-for="s in getGroupSearches(group.searches)"
                  :key="s.id"
                  :search="s"
                  :show-inline-chart="showInlineChartFor(s)"
                  :compact-layout="compactBlocks" />
              </div>
            </div>
          </div>

          <template v-else>
            <div class="flex min-w-0 w-full max-w-full flex-col gap-6">
              <div
                v-if="getSensorSearches(group.searches).length > 0"
                :class="groupBlocksLayoutClass(getSensorSearches(group.searches).length)">
                <DashboardBlock
                  v-for="s in getSensorSearches(group.searches)"
                  :key="s.id"
                  :search="s"
                  :show-inline-chart="showInlineChartFor(s)"
                  :compact-layout="compactBlocks" />
              </div>
              <DashboardUnifiedChart
                v-if="unifiedKeySet.has(group.periodKey)"
                :period-key="group.periodKey"
                :compact-layout="compactBlocks" />
              <div
                v-if="getGroupSearches(group.searches).length > 0"
                :class="groupBlocksLayoutClass(getGroupSearches(group.searches).length)">
                <DashboardBlock
                  v-for="s in getGroupSearches(group.searches)"
                  :key="s.id"
                  :search="s"
                  :show-inline-chart="showInlineChartFor(s)"
                  :compact-layout="compactBlocks" />
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.dashboard-period-groups-weighted {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

@media (min-width: 1024px) {
  .dashboard-period-groups-weighted {
    grid-template-columns: var(--dashboard-pg-cols, repeat(1, minmax(0, 1fr)));
  }
}
</style>
